/**
 * Global WebSocket Manager
 * 用于全局实时功能(移动端绑定、通知等),不依赖 Vue 组件生命周期
 */

import { ref } from 'vue'
import { Message } from '@arco-design/web-vue'

export interface WebSocketMessage {
  type: string
  data: any
  timestamp: string
  jobId?: string
}

let ws: WebSocket | null = null
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let reconnectAttempts = 0
const RECONNECT_DELAY_BASE = 3000

// Global state
export const isConnected = ref(false)
export const isReconnecting = ref(false)

// Store for event listeners
const eventListeners = new Map<string, Set<(data: any) => void>>()

/**
 * 解析WebSocket URL
 */
function resolveWebSocketUrl(): string {
  const envWsUrl = (import.meta as any).env?.VITE_WS_URL?.trim()

  // 优先使用环境变量配置的 WebSocket 地址
  if (envWsUrl) {
    if (envWsUrl.startsWith('/')) {
      // 相对路径,需要构建完整 URL
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const host = (import.meta as any).env?.VITE_DEV_API_BASE_URL || window.location.hostname + ':8080'
      return `${protocol}//${host}${envWsUrl}`
    }
    return envWsUrl
  }

  // 开发环境:直接连接到后端服务器(不是 Vite dev server)
  if (import.meta.env.DEV) {
    const backendHost = (import.meta as any).env?.VITE_DEV_API_BASE_URL || 'localhost:8080'
    return `ws://${backendHost}/ws`
  }

  // 生产环境:使用当前页面的 host
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${window.location.host}/ws`
}

/**
 * 处理连接成功
 */
function handleOpen() {
  console.log('[Global WebSocket] 连接成功')
  isConnected.value = true
  isReconnecting.value = false
  reconnectAttempts = 0
}

/**
 * 处理接收到的消息
 */
function handleMessage(event: MessageEvent) {
  try {
    const message: WebSocketMessage = JSON.parse(event.data)
    console.log('[Global WebSocket] 收到消息:', message)

    switch (message.type) {
      case 'connection':
        console.log('[Global WebSocket] 连接确认:', message.data)
        break

      case 'bind_status_updated': {
        console.log('[Global WebSocket] 绑定状态更新:', message.data)
        // 通过 CustomEvent 分发给组件监听
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('bind-status-updated', { detail: message.data }))

          // 如果绑定完成，触发专门的绑定完成事件
          if (message.data.status === 'confirmed') {
            window.dispatchEvent(new CustomEvent('bind-completed', { detail: message.data }))
            // 显示成功提示
            Message.success('设备绑定成功！')
          }
          // 如果重新激活，触发重新绑定完成事件
          else if (message.data.status === 'reactivated') {
            window.dispatchEvent(new CustomEvent('bind-reactivated', { detail: message.data }))
            // 显示成功提示
            Message.success('设备重新绑定成功！')
          }
          // 如果绑定取消，触发取消事件
          else if (message.data.status === 'cancelled') {
            window.dispatchEvent(new CustomEvent('bind-cancelled', { detail: message.data }))
          }
        }
        // 触发注册的事件监听器
        const listeners = eventListeners.get('bind_status_updated')
        if (listeners) {
          listeners.forEach(callback => callback(message.data))
        }
        break
      }

      case 'error':
        console.error('[Global WebSocket] 服务器错误:', message.data)
        Message.error(`WebSocket错误: ${message.data.message}`)
        break

      case 'vl_batch_event': {
        const { event, batchId, errorCode } = message.data
        console.log('[Global WebSocket] VL批次事件:', message.data)
        // 分发给具体页面细粒度处理
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('vl-batch-event', { detail: message.data }))
        }
        // 触发注册的事件监听器
        const batchListeners = eventListeners.get('vl_batch_event')
        if (batchListeners) {
          batchListeners.forEach(callback => callback(message.data))
        }
        // 全局 toast 提示
        if (event === 'BATCH_NEEDS_REVIEW') {
          Message.warning(`批次 ${batchId} 需要核对，请前往 Web 端处理`)
        } else if (event === 'BATCH_FINALIZE_FAILED') {
          Message.error(`批次 ${batchId} 建档失败${errorCode ? `（${errorCode}）` : ''}`)
        }
        break
      }

      default:
        console.log('[Global WebSocket] 未处理的消息类型:', message.type)
    }
  } catch (error) {
    console.error('[Global WebSocket] 解析消息失败:', error)
  }
}

/**
 * 处理连接关闭
 */
function handleClose() {
  console.log('[Global WebSocket] 连接关闭')
  isConnected.value = false
  ws = null
  scheduleReconnect()
}

/**
 * 处理连接错误
 */
function handleError(error: Event) {
  console.error('[Global WebSocket] 连接错误:', error)
  isConnected.value = false
  // 添加更详细的错误信息
  console.error('[Global WebSocket] 错误详情:', {
    type: error.type,
    target: (error.target as WebSocket)?.readyState,
    url: (error.target as WebSocket)?.url
  })
}

/**
 * 安排重连
 */
function scheduleReconnect() {
  if (reconnectTimer) return

  isReconnecting.value = true
  const delay = Math.min(RECONNECT_DELAY_BASE * Math.pow(2, reconnectAttempts), 30000)

  console.log(`[Global WebSocket] ${delay}ms 后重连... (尝试 ${reconnectAttempts + 1})`)

  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    reconnectAttempts++
    connect()
  }, delay)
}

/**
 * 连接 WebSocket
 */
export function connect() {
  // 如果已经连接或正在重连,不重复连接
  if (ws?.readyState === WebSocket.OPEN || isReconnecting.value) {
    console.log('[Global WebSocket] 已连接或正在重连,跳过')
    return
  }

  try {
    let wsUrl = resolveWebSocketUrl()

    // 添加认证 token 到 query 参数
    const token = localStorage.getItem('token')
    if (token) {
      const separator = wsUrl.includes('?') ? '&' : '?'
      wsUrl = `${wsUrl}${separator}token=${encodeURIComponent(token)}`
      console.log('[Global WebSocket] 使用认证 token 连接')
    } else {
      console.log('[Global WebSocket] 未找到 token,使用匿名连接')
    }

    console.log('[Global WebSocket] 正在连接:', wsUrl.replace(/token=[^&]+/, 'token=***'))

    // 创建 WebSocket 连接
    ws = new WebSocket(wsUrl)

    // 打印 WebSocket 状态变化
    console.log('[Global WebSocket] WebSocket 已创建, readyState:', ws.readyState)

    ws.onopen = handleOpen
    ws.onmessage = handleMessage
    ws.onclose = handleClose
    ws.onerror = handleError

    // 监听 readyState 变化
    const checkState = setInterval(() => {
      if (ws) {
        console.log('[Global WebSocket] readyState:', ws.readyState, '(0=CONNECTING, 1=OPEN, 2=CLOSING, 3=CLOSED)')
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CLOSED) {
          clearInterval(checkState)
        }
      } else {
        clearInterval(checkState)
      }
    }, 500)
  } catch (error) {
    console.error('[Global WebSocket] 连接失败:', error)
    scheduleReconnect()
  }
}

/**
 * 断开连接
 */
export function disconnect() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }

  if (ws) {
    ws.close()
    ws = null
  }

  isConnected.value = false
  isReconnecting.value = false
}

/**
 * 添加事件监听器
 */
export function addEventListener(event: string, callback: (data: any) => void) {
  if (!eventListeners.has(event)) {
    eventListeners.set(event, new Set())
  }
  eventListeners.get(event)!.add(callback)

  // 返回清理函数
  return () => {
    const listeners = eventListeners.get(event)
    if (listeners) {
      listeners.delete(callback)
    }
  }
}

/**
 * 移除事件监听器
 */
export function removeEventListener(event: string, callback: (data: any) => void) {
  const listeners = eventListeners.get(event)
  if (listeners) {
    listeners.delete(callback)
  }
}

/**
 * 初始化全局 WebSocket (在应用启动时调用)
 */
export function initGlobalWebSocket() {
  const token = localStorage.getItem('token')
  if (token) {
    console.log('[Global WebSocket] 发现 token,启动连接...')
    connect()
  } else {
    console.log('[Global WebSocket] 无 token,等待登录后连接')
  }
}
